import { useState } from "react";
import ArchiveOutlined from "@mui/icons-material/ArchiveOutlined";
import Check from "@mui/icons-material/Check";
import Checklist from "@mui/icons-material/Checklist";
import Clear from "@mui/icons-material/Clear";
import DoNotDisturb from "@mui/icons-material/DoNotDisturb";
import { MenuItem, TextField } from "@mui/material";
import SimpleDialog from "components/SimpleDialog";
import TableRowAction from "components/TableRowAction";
import TableRowMenu from "components/TableRowMenu";
import { Company, OnboardingState, OnboardingStateValues } from "models/Company";
import {
  useApproveCompanyMutation,
  useArchiveCompanyMutation,
  useBlockCompanyMutation,
  useDenyCompanyMutation,
  useRestoreCompanyMutation,
  useUnblockCompanyMutation,
  useUpdateOnboardingStatusMutation,
} from "app/api/companies";
import { Replay, UnarchiveOutlined } from "@mui/icons-material";

type CompaniesTableActionsProps = { company: Company };

const CompaniesTableActions: React.FC<CompaniesTableActionsProps> = (props) => {
  const { company } = props;

  const [showStatusChangeDialog, setShowStatusChangeDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showDenyDialog, setShowDenyDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [showUnblockDialog, setShowUnblockDialog] = useState(false);

  const [approveCompany] = useApproveCompanyMutation();
  const [denyCompany] = useDenyCompanyMutation();
  const [archiveCompany] = useArchiveCompanyMutation();
  const [restoreCompany] = useRestoreCompanyMutation();
  const [blockCompany] = useBlockCompanyMutation();
  const [unblockCompany] = useUnblockCompanyMutation();
  const [updateOnboardingStatus] = useUpdateOnboardingStatusMutation();

  return (
    <>
      <TableRowMenu>
        {company.state === "NEW" && (
          <>
            <TableRowAction
              icon={<Checklist />}
              text="Ändra status"
              onClick={() => setShowStatusChangeDialog(true)}
            />
            <TableRowAction
              icon={<Check />}
              text="Godkänn"
              onClick={() => setShowApproveDialog(true)}
            />
            <TableRowAction icon={<Clear />} text="Neka" onClick={() => setShowDenyDialog(true)} />
          </>
        )}
        {company.state === "ACTIVE" && (
          <TableRowAction
            icon={<ArchiveOutlined />}
            text="Arkivera"
            onClick={() => setShowArchiveDialog(true)}
          />
        )}
        {company.state === "ARCHIVED" && (
          <>
            <TableRowAction
              icon={<UnarchiveOutlined />}
              text="Återställ"
              onClick={() => setShowRestoreDialog(true)}
            />
            <TableRowAction
              icon={<DoNotDisturb />}
              text="Blockera"
              onClick={() => setShowBlockDialog(true)}
            />
          </>
        )}
        {company.state === "BLOCKED" && (
          <TableRowAction
            icon={<Replay />}
            text="Avblockera"
            onClick={() => setShowUnblockDialog(true)}
          />
        )}
      </TableRowMenu>

      {showStatusChangeDialog && (
        <SimpleDialog
          title="Ändra onboarding-status"
          onClose={() => setShowStatusChangeDialog(false)}
          onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.currentTarget as HTMLFormElement);

            updateOnboardingStatus({
              id: company.id,
              onboardingState: formData.get("onboardingState") as OnboardingState,
            })
              .unwrap()
              .then(() => {
                setShowStatusChangeDialog(false);
              });
          }}
        >
          <TextField
            name="onboardingState"
            label="Status"
            variant="filled"
            fullWidth
            margin="normal"
            select
            defaultValue={company.onboardingState}
          >
            {Object.entries(OnboardingStateValues).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </SimpleDialog>
      )}

      {showApproveDialog && (
        <SimpleDialog
          title="Godkänn företag"
          onClose={() => setShowApproveDialog(false)}
          onConfirm={() => {
            approveCompany({ companyId: company.id })
              .unwrap()
              .then(() => {
                setShowApproveDialog(false);
              });
          }}
        >
          Företaget aktiveras och kan börja användas för bokning.
        </SimpleDialog>
      )}

      {showDenyDialog && (
        <SimpleDialog
          title="Neka företag"
          onClose={() => setShowDenyDialog(false)}
          onConfirm={() => {
            denyCompany({ companyId: company.id })
              .unwrap()
              .then(() => {
                setShowDenyDialog(false);
              });
          }}
        >
          Företaget arkiveras och kommer inte att vara tillgängligt för bokning.
        </SimpleDialog>
      )}

      {showArchiveDialog && (
        <SimpleDialog
          title="Arkivera företag"
          onClose={() => setShowArchiveDialog(false)}
          onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.currentTarget as HTMLFormElement);

            archiveCompany({ companyId: company.id, reason: formData.get("reason") as string })
              .unwrap()
              .then(() => {
                setShowArchiveDialog(false);
              });
          }}
        >
          Företaget arkiveras och kan inte längre användas för bokning.
          <TextField
            name="reason"
            label="Anledning"
            variant="filled"
            fullWidth
            margin="normal"
            multiline
            required
            rows={3}
            helperText="Max 1000 tecken."
            slotProps={{
              htmlInput: {
                maxLength: 1000,
              },
            }}
          />
        </SimpleDialog>
      )}

      {showRestoreDialog && (
        <SimpleDialog
          title="Återställ företag"
          onClose={() => setShowRestoreDialog(false)}
          onConfirm={() => {
            restoreCompany({ companyId: company.id })
              .unwrap()
              .then(() => {
                setShowRestoreDialog(false);
              });
          }}
        >
          Företaget aktiveras och kan återigen användas för bokning.
        </SimpleDialog>
      )}

      {showBlockDialog && (
        <SimpleDialog
          title="Blockera företag"
          onClose={() => setShowBlockDialog(false)}
          onConfirm={() => {
            blockCompany({ companyId: company.id })
              .unwrap()
              .then(() => {
                setShowBlockDialog(false);
              });
          }}
        >
          Företaget blockeras och kan endast återställas av admin.
        </SimpleDialog>
      )}

      {showUnblockDialog && (
        <SimpleDialog
          title="Unblockera företag"
          onClose={() => setShowUnblockDialog(false)}
          onConfirm={() => {
            unblockCompany({ companyId: company.id })
              .unwrap()
              .then(() => {
                setShowUnblockDialog(false);
              });
          }}
        >
          Företaget återställs och kan återigen användas för bokning.
        </SimpleDialog>
      )}
    </>
  );
};

export default CompaniesTableActions;
